import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { usePageT } from './i18n/LanguageContext';
import { LanguageSwitcher } from './components/LanguageSwitcher';

const API = (process.env.REACT_APP_API_URL || 'https://api.nautiqo.cloud') + '/api';
const WHATSAPP_NUMBER = process.env.REACT_APP_NAUTIQO_WHATSAPP || '393793782759';

const translations = {
  it: {
    // Navbar
    navAlreadyRegistered: 'Già registrato?',
    navLogin: 'Accedi',
    // Hero
    heroBadge: '🚀 Prova gratuita 30 giorni',
    heroTitleLine1: 'Gestisci il tuo',
    heroTitleLine2: 'charter con Nautiqo',
    heroSubtitle: "Prenotazioni, flotta, clienti, disponibilità e pagamenti in un'unica piattaforma pensata per chi noleggia barche.",
    feature1: 'Calendario sempre aggiornato',
    feature2: 'Nessuna doppia prenotazione',
    feature3: 'Link pubblico per ricevere richieste',
    feature4: 'Gestione clienti e pagamenti online',
    feature5: 'Prova gratuita di 30 giorni, nessuna carta',
    // Success state
    successTitle: 'Richiesta salvata!',
    successSubtitle: 'Ora invia il messaggio WhatsApp già compilato per parlare subito con noi.',
    openWhatsapp: '📱 Apri WhatsApp',
    successHint1: 'Il messaggio è già compilato con i tuoi dati, premi solo',
    successHint2: 'Invia',
    // Form
    formTitle: 'Richiedi informazioni',
    formSubtitle: 'Compila il modulo e avvia subito una chat WhatsApp con il nostro team.',
    labelBusinessName: 'Nome attività / azienda',
    placeholderBusinessName: 'es. Seawave Charter',
    labelContactName: 'Nome e cognome referente',
    placeholderContactName: 'es. Mario Rossi',
    labelPhone: 'Telefono',
    placeholderPhone: '+39 333 1234567',
    labelEmail: 'Email',
    placeholderEmail: 'mario@esempio.it',
    labelActivityType: 'Tipo attività',
    selectPlaceholder: 'Seleziona...',
    labelNumberBoats: 'Numero barche / gommoni',
    placeholderNumberBoats: 'es. 3',
    labelUsesSoftware: 'Usi già un gestionale?',
    softwareNo: 'No',
    softwareYes: 'Sì',
    softwareYesUnhappy: 'Sì, ma non mi trovo bene',
    labelCurrentSoftware: 'Quale gestionale usi?',
    placeholderCurrentSoftware: 'es. Booking.com, Excel, altro...',
    labelMessage: 'Messaggio / note',
    optional: '(opzionale)',
    placeholderMessage: 'Hai domande specifiche o vuoi raccontarci la tua situazione...',
    privacyText: 'Acconsento al trattamento dei dati per essere ricontattato da Nautiqo.',
    privacyPolicy: 'Privacy policy',
    waPreviewTitle: '📱 Anteprima messaggio WhatsApp',
    submitting: 'Salvataggio...',
    submitButton: '📱 Invia e avvia la chat WhatsApp',
    // Activity types
    typeRibRental: 'Noleggio gommoni',
    typeBoatCharter: 'Charter barche',
    typeExcursions: 'Escursioni / tour in barca',
    typeRentalAndTour: 'Noleggio + tour',
    typeOther: 'Altro',
    // Validation errors
    errRequired: 'Campo obbligatorio',
    errInvalidEmail: 'Email non valida',
    errSelectType: 'Seleziona un tipo',
    errInvalidNumber: 'Inserisci un numero valido',
    errAcceptPrivacy: 'Devi accettare la privacy policy',
    errSaveGeneric: 'Errore nel salvataggio. Riprova.',
    // WhatsApp message
    waGreeting: 'Ciao Nautiqo, ho appena compilato il modulo e vorrei ricevere informazioni.',
    waBusinessName: 'Nome attività',
    waContact: 'Referente',
    waPhone: 'Telefono',
    waEmail: 'Email',
    waActivityType: 'Tipo attività',
    waNumberBoats: 'Numero barche/gommoni',
    waCurrentSoftware: 'Gestionale attuale',
    waNotes: 'Note',
    waNone: 'Nessuna',
    waNotSpecified: 'non specificato',
    waSoftwareNo: 'No',
    waSoftwareYes: 'Sì',
    waSoftwareYesUnhappy: 'Sì, ma non mi trovo bene',
    waClosing: 'Vorrei capire come funziona Nautiqo e come attivare la prova gratuita.',
  },
  en: {
    navAlreadyRegistered: 'Already registered?',
    navLogin: 'Log in',
    heroBadge: '🚀 30-day free trial',
    heroTitleLine1: 'Manage your',
    heroTitleLine2: 'charter with Nautiqo',
    heroSubtitle: 'Bookings, fleet, customers, availability and payments in one platform built for boat rental businesses.',
    feature1: 'Always up-to-date calendar',
    feature2: 'No double bookings',
    feature3: 'Public link to receive requests',
    feature4: 'Customer management and online payments',
    feature5: '30-day free trial, no card required',
    successTitle: 'Request saved!',
    successSubtitle: 'Now send the pre-filled WhatsApp message to talk to us right away.',
    openWhatsapp: '📱 Open WhatsApp',
    successHint1: 'The message is already filled in with your details, just press',
    successHint2: 'Send',
    formTitle: 'Request information',
    formSubtitle: 'Fill out the form and instantly start a WhatsApp chat with our team.',
    labelBusinessName: 'Business / company name',
    placeholderBusinessName: 'e.g. Seawave Charter',
    labelContactName: 'Contact full name',
    placeholderContactName: 'e.g. John Smith',
    labelPhone: 'Phone',
    placeholderPhone: '+1 555 123 4567',
    labelEmail: 'Email',
    placeholderEmail: 'john@example.com',
    labelActivityType: 'Business type',
    selectPlaceholder: 'Select...',
    labelNumberBoats: 'Number of boats / dinghies',
    placeholderNumberBoats: 'e.g. 3',
    labelUsesSoftware: 'Do you already use management software?',
    softwareNo: 'No',
    softwareYes: 'Yes',
    softwareYesUnhappy: "Yes, but I'm not happy with it",
    labelCurrentSoftware: 'Which software do you use?',
    placeholderCurrentSoftware: 'e.g. Booking.com, Excel, other...',
    labelMessage: 'Message / notes',
    optional: '(optional)',
    placeholderMessage: 'Do you have specific questions or want to tell us about your situation...',
    privacyText: 'I consent to the processing of my data to be contacted by Nautiqo.',
    privacyPolicy: 'Privacy policy',
    waPreviewTitle: '📱 WhatsApp message preview',
    submitting: 'Saving...',
    submitButton: '📱 Send and start the WhatsApp chat',
    typeRibRental: 'Dinghy / RIB rental',
    typeBoatCharter: 'Boat charter',
    typeExcursions: 'Boat excursions / tours',
    typeRentalAndTour: 'Rental + tours',
    typeOther: 'Other',
    errRequired: 'Required field',
    errInvalidEmail: 'Invalid email',
    errSelectType: 'Select a type',
    errInvalidNumber: 'Enter a valid number',
    errAcceptPrivacy: 'You must accept the privacy policy',
    errSaveGeneric: 'Error while saving. Please try again.',
    waGreeting: "Hi Nautiqo, I've just filled out the form and would like to receive information.",
    waBusinessName: 'Business name',
    waContact: 'Contact',
    waPhone: 'Phone',
    waEmail: 'Email',
    waActivityType: 'Business type',
    waNumberBoats: 'Number of boats/dinghies',
    waCurrentSoftware: 'Current software',
    waNotes: 'Notes',
    waNone: 'None',
    waNotSpecified: 'not specified',
    waSoftwareNo: 'No',
    waSoftwareYes: 'Yes',
    waSoftwareYesUnhappy: "Yes, but I'm not happy with it",
    waClosing: 'I would like to understand how Nautiqo works and how to start the free trial.',
  },
  es: {
    navAlreadyRegistered: '¿Ya estás registrado?',
    navLogin: 'Iniciar sesión',
    heroBadge: '🚀 Prueba gratuita de 30 días',
    heroTitleLine1: 'Gestiona tu',
    heroTitleLine2: 'charter con Nautiqo',
    heroSubtitle: 'Reservas, flota, clientes, disponibilidad y pagos en una única plataforma pensada para empresas de alquiler de barcos.',
    feature1: 'Calendario siempre actualizado',
    feature2: 'Sin reservas duplicadas',
    feature3: 'Enlace público para recibir solicitudes',
    feature4: 'Gestión de clientes y pagos online',
    feature5: 'Prueba gratuita de 30 días, sin tarjeta',
    successTitle: '¡Solicitud guardada!',
    successSubtitle: 'Ahora envía el mensaje de WhatsApp ya preparado para hablar con nosotros de inmediato.',
    openWhatsapp: '📱 Abrir WhatsApp',
    successHint1: 'El mensaje ya está rellenado con tus datos, solo pulsa',
    successHint2: 'Enviar',
    formTitle: 'Solicitar información',
    formSubtitle: 'Rellena el formulario e inicia al instante un chat de WhatsApp con nuestro equipo.',
    labelBusinessName: 'Nombre del negocio / empresa',
    placeholderBusinessName: 'p. ej. Seawave Charter',
    labelContactName: 'Nombre y apellidos del responsable',
    placeholderContactName: 'p. ej. Juan Pérez',
    labelPhone: 'Teléfono',
    placeholderPhone: '+34 600 123 456',
    labelEmail: 'Email',
    placeholderEmail: 'juan@ejemplo.com',
    labelActivityType: 'Tipo de actividad',
    selectPlaceholder: 'Selecciona...',
    labelNumberBoats: 'Número de barcos / neumáticas',
    placeholderNumberBoats: 'p. ej. 3',
    labelUsesSoftware: '¿Ya usas un software de gestión?',
    softwareNo: 'No',
    softwareYes: 'Sí',
    softwareYesUnhappy: 'Sí, pero no me convence',
    labelCurrentSoftware: '¿Qué software usas?',
    placeholderCurrentSoftware: 'p. ej. Booking.com, Excel, otro...',
    labelMessage: 'Mensaje / notas',
    optional: '(opcional)',
    placeholderMessage: '¿Tienes preguntas concretas o quieres contarnos tu situación...',
    privacyText: 'Consiento el tratamiento de mis datos para ser contactado por Nautiqo.',
    privacyPolicy: 'Política de privacidad',
    waPreviewTitle: '📱 Vista previa del mensaje de WhatsApp',
    submitting: 'Guardando...',
    submitButton: '📱 Enviar e iniciar el chat de WhatsApp',
    typeRibRental: 'Alquiler de neumáticas',
    typeBoatCharter: 'Charter de barcos',
    typeExcursions: 'Excursiones / tours en barco',
    typeRentalAndTour: 'Alquiler + tours',
    typeOther: 'Otro',
    errRequired: 'Campo obligatorio',
    errInvalidEmail: 'Email no válido',
    errSelectType: 'Selecciona un tipo',
    errInvalidNumber: 'Introduce un número válido',
    errAcceptPrivacy: 'Debes aceptar la política de privacidad',
    errSaveGeneric: 'Error al guardar. Inténtalo de nuevo.',
    waGreeting: 'Hola Nautiqo, acabo de rellenar el formulario y me gustaría recibir información.',
    waBusinessName: 'Nombre del negocio',
    waContact: 'Responsable',
    waPhone: 'Teléfono',
    waEmail: 'Email',
    waActivityType: 'Tipo de actividad',
    waNumberBoats: 'Número de barcos/neumáticas',
    waCurrentSoftware: 'Software actual',
    waNotes: 'Notas',
    waNone: 'Ninguna',
    waNotSpecified: 'no especificado',
    waSoftwareNo: 'No',
    waSoftwareYes: 'Sí',
    waSoftwareYesUnhappy: 'Sí, pero no me convence',
    waClosing: 'Me gustaría entender cómo funciona Nautiqo y cómo activar la prueba gratuita.',
  },
  fr: {
    navAlreadyRegistered: 'Déjà inscrit ?',
    navLogin: 'Se connecter',
    heroBadge: '🚀 Essai gratuit de 30 jours',
    heroTitleLine1: 'Gérez votre',
    heroTitleLine2: 'charter avec Nautiqo',
    heroSubtitle: 'Réservations, flotte, clients, disponibilités et paiements sur une seule plateforme pensée pour les professionnels de la location de bateaux.',
    feature1: 'Calendrier toujours à jour',
    feature2: 'Aucune double réservation',
    feature3: 'Lien public pour recevoir des demandes',
    feature4: 'Gestion des clients et paiements en ligne',
    feature5: 'Essai gratuit de 30 jours, sans carte',
    successTitle: 'Demande enregistrée !',
    successSubtitle: 'Envoyez maintenant le message WhatsApp pré-rempli pour parler avec nous tout de suite.',
    openWhatsapp: '📱 Ouvrir WhatsApp',
    successHint1: 'Le message est déjà pré-rempli avec vos informations, appuyez simplement sur',
    successHint2: 'Envoyer',
    formTitle: 'Demander des informations',
    formSubtitle: 'Remplissez le formulaire et démarrez immédiatement une discussion WhatsApp avec notre équipe.',
    labelBusinessName: "Nom de l'activité / société",
    placeholderBusinessName: 'ex. Seawave Charter',
    labelContactName: 'Nom et prénom du contact',
    placeholderContactName: 'ex. Jean Dupont',
    labelPhone: 'Téléphone',
    placeholderPhone: '+33 6 12 34 56 78',
    labelEmail: 'Email',
    placeholderEmail: 'jean@exemple.fr',
    labelActivityType: "Type d'activité",
    selectPlaceholder: 'Sélectionner...',
    labelNumberBoats: 'Nombre de bateaux / semi-rigides',
    placeholderNumberBoats: 'ex. 3',
    labelUsesSoftware: 'Utilisez-vous déjà un logiciel de gestion ?',
    softwareNo: 'Non',
    softwareYes: 'Oui',
    softwareYesUnhappy: "Oui, mais je n'en suis pas satisfait",
    labelCurrentSoftware: 'Quel logiciel utilisez-vous ?',
    placeholderCurrentSoftware: 'ex. Booking.com, Excel, autre...',
    labelMessage: 'Message / notes',
    optional: '(facultatif)',
    placeholderMessage: 'Vous avez des questions précises ou souhaitez nous parler de votre situation...',
    privacyText: 'Je consens au traitement de mes données pour être recontacté par Nautiqo.',
    privacyPolicy: 'Politique de confidentialité',
    waPreviewTitle: '📱 Aperçu du message WhatsApp',
    submitting: 'Enregistrement...',
    submitButton: '📱 Envoyer et démarrer la discussion WhatsApp',
    typeRibRental: 'Location de semi-rigides',
    typeBoatCharter: 'Charter de bateaux',
    typeExcursions: 'Excursions / tours en bateau',
    typeRentalAndTour: 'Location + tours',
    typeOther: 'Autre',
    errRequired: 'Champ obligatoire',
    errInvalidEmail: 'Email invalide',
    errSelectType: 'Sélectionnez un type',
    errInvalidNumber: 'Entrez un nombre valide',
    errAcceptPrivacy: 'Vous devez accepter la politique de confidentialité',
    errSaveGeneric: "Erreur lors de l'enregistrement. Veuillez réessayer.",
    waGreeting: 'Bonjour Nautiqo, je viens de remplir le formulaire et je souhaiterais recevoir des informations.',
    waBusinessName: "Nom de l'activité",
    waContact: 'Contact',
    waPhone: 'Téléphone',
    waEmail: 'Email',
    waActivityType: "Type d'activité",
    waNumberBoats: 'Nombre de bateaux/semi-rigides',
    waCurrentSoftware: 'Logiciel actuel',
    waNotes: 'Notes',
    waNone: 'Aucune',
    waNotSpecified: 'non spécifié',
    waSoftwareNo: 'Non',
    waSoftwareYes: 'Oui',
    waSoftwareYesUnhappy: "Oui, mais je n'en suis pas satisfait",
    waClosing: "Je souhaiterais comprendre comment fonctionne Nautiqo et comment activer l'essai gratuit.",
  },
  de: {
    navAlreadyRegistered: 'Bereits registriert?',
    navLogin: 'Anmelden',
    heroBadge: '🚀 30 Tage kostenlos testen',
    heroTitleLine1: 'Verwalte deinen',
    heroTitleLine2: 'Charter mit Nautiqo',
    heroSubtitle: 'Buchungen, Flotte, Kunden, Verfügbarkeit und Zahlungen auf einer Plattform, die für Bootsvermieter entwickelt wurde.',
    feature1: 'Immer aktueller Kalender',
    feature2: 'Keine Doppelbuchungen',
    feature3: 'Öffentlicher Link für Anfragen',
    feature4: 'Kundenverwaltung und Online-Zahlungen',
    feature5: '30 Tage kostenlose Testversion, keine Karte nötig',
    successTitle: 'Anfrage gespeichert!',
    successSubtitle: 'Sende jetzt die bereits ausgefüllte WhatsApp-Nachricht, um sofort mit uns zu sprechen.',
    openWhatsapp: '📱 WhatsApp öffnen',
    successHint1: 'Die Nachricht ist bereits mit deinen Daten ausgefüllt, drücke einfach',
    successHint2: 'Senden',
    formTitle: 'Informationen anfordern',
    formSubtitle: 'Fülle das Formular aus und starte sofort einen WhatsApp-Chat mit unserem Team.',
    labelBusinessName: 'Name des Unternehmens',
    placeholderBusinessName: 'z. B. Seawave Charter',
    labelContactName: 'Vor- und Nachname des Ansprechpartners',
    placeholderContactName: 'z. B. Max Mustermann',
    labelPhone: 'Telefon',
    placeholderPhone: '+49 151 12345678',
    labelEmail: 'E-Mail',
    placeholderEmail: 'max@beispiel.de',
    labelActivityType: 'Art der Tätigkeit',
    selectPlaceholder: 'Auswählen...',
    labelNumberBoats: 'Anzahl Boote / Schlauchboote',
    placeholderNumberBoats: 'z. B. 3',
    labelUsesSoftware: 'Nutzt du bereits eine Verwaltungssoftware?',
    softwareNo: 'Nein',
    softwareYes: 'Ja',
    softwareYesUnhappy: 'Ja, aber ich bin nicht zufrieden',
    labelCurrentSoftware: 'Welche Software nutzt du?',
    placeholderCurrentSoftware: 'z. B. Booking.com, Excel, andere...',
    labelMessage: 'Nachricht / Anmerkungen',
    optional: '(optional)',
    placeholderMessage: 'Hast du konkrete Fragen oder möchtest du uns deine Situation schildern...',
    privacyText: 'Ich willige in die Verarbeitung meiner Daten ein, um von Nautiqo kontaktiert zu werden.',
    privacyPolicy: 'Datenschutzerklärung',
    waPreviewTitle: '📱 Vorschau der WhatsApp-Nachricht',
    submitting: 'Wird gespeichert...',
    submitButton: '📱 Senden und WhatsApp-Chat starten',
    typeRibRental: 'Schlauchboot-Vermietung',
    typeBoatCharter: 'Boots-Charter',
    typeExcursions: 'Ausflüge / Bootstouren',
    typeRentalAndTour: 'Vermietung + Touren',
    typeOther: 'Sonstiges',
    errRequired: 'Pflichtfeld',
    errInvalidEmail: 'Ungültige E-Mail',
    errSelectType: 'Wähle einen Typ',
    errInvalidNumber: 'Gib eine gültige Zahl ein',
    errAcceptPrivacy: 'Du musst die Datenschutzerklärung akzeptieren',
    errSaveGeneric: 'Fehler beim Speichern. Bitte versuche es erneut.',
    waGreeting: 'Hallo Nautiqo, ich habe gerade das Formular ausgefüllt und würde gerne Informationen erhalten.',
    waBusinessName: 'Name des Unternehmens',
    waContact: 'Ansprechpartner',
    waPhone: 'Telefon',
    waEmail: 'E-Mail',
    waActivityType: 'Art der Tätigkeit',
    waNumberBoats: 'Anzahl Boote/Schlauchboote',
    waCurrentSoftware: 'Aktuelle Software',
    waNotes: 'Anmerkungen',
    waNone: 'Keine',
    waNotSpecified: 'nicht angegeben',
    waSoftwareNo: 'Nein',
    waSoftwareYes: 'Ja',
    waSoftwareYesUnhappy: 'Ja, aber ich bin nicht zufrieden',
    waClosing: 'Ich möchte verstehen, wie Nautiqo funktioniert und wie ich die kostenlose Testversion aktiviere.',
  },
};

// Canonical Italian values stored in the backend payload, keyed by the
// translation key used to render the label.
const TIPO_CANONICAL = {
  typeRibRental: 'Noleggio gommoni',
  typeBoatCharter: 'Charter barche',
  typeExcursions: 'Escursioni / tour in barca',
  typeRentalAndTour: 'Noleggio + tour',
  typeOther: 'Altro',
};

function trackEvent(name, params = {}) {
  try { if (window.gtag) window.gtag('event', name, params); } catch {}
  try { if (window.fbq) window.fbq('trackCustom', name, params); } catch {}
}

function trackLead(params = {}) {
  // Standard Meta event — used by Meta Ads for campaign optimization
  try { if (window.fbq) window.fbq('track', 'Lead', params); } catch {}
  try { if (window.gtag) window.gtag('event', 'generate_lead', params); } catch {}
}

const inp = 'w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 text-sm outline-none focus:border-blue-500 transition';
const inpErr = 'w-full px-4 py-3 bg-slate-800 border border-red-500 rounded-xl text-white placeholder-slate-500 text-sm outline-none focus:border-red-400 transition';
const lbl = 'block text-sm font-medium text-slate-300 mb-1.5';

function buildWaMessage(form, t) {
  const notSpec = t.waNotSpecified;
  const gestionale =
    form.usa_gestionale === 'no' ? t.waSoftwareNo
    : form.usa_gestionale === 'si' ? `${t.waSoftwareYes} (${form.gestionale_attuale || notSpec})`
    : `${t.waSoftwareYesUnhappy} (${form.gestionale_attuale || notSpec})`;
  return [
    t.waGreeting,
    '',
    `${t.waBusinessName}: ${form.nome_attivita}`,
    `${t.waContact}: ${form.nome_referente}`,
    `${t.waPhone}: ${form.telefono}`,
    `${t.waEmail}: ${form.email}`,
    `${t.waActivityType}: ${form.tipo_attivita_label || form.tipo_attivita}`,
    `${t.waNumberBoats}: ${form.numero_barche}`,
    `${t.waCurrentSoftware}: ${gestionale}`,
    `${t.waNotes}: ${form.messaggio || t.waNone}`,
    '',
    t.waClosing,
  ].join('\n');
}

export default function LeadPage() {
  const t = usePageT(translations);
  const TIPI_ATTIVITA = [
    { value: TIPO_CANONICAL.typeRibRental, label: t.typeRibRental },
    { value: TIPO_CANONICAL.typeBoatCharter, label: t.typeBoatCharter },
    { value: TIPO_CANONICAL.typeExcursions, label: t.typeExcursions },
    { value: TIPO_CANONICAL.typeRentalAndTour, label: t.typeRentalAndTour },
    { value: TIPO_CANONICAL.typeOther, label: t.typeOther },
  ];
  const [searchParams] = useSearchParams();
  const utms = {
    utm_source: searchParams.get('utm_source') || '',
    utm_medium: searchParams.get('utm_medium') || '',
    utm_campaign: searchParams.get('utm_campaign') || '',
    utm_content: searchParams.get('utm_content') || '',
    utm_term: searchParams.get('utm_term') || '',
  };

  const [form, setForm] = useState({
    nome_attivita: '',
    nome_referente: '',
    telefono: '',
    email: '',
    tipo_attivita: '',
    tipo_attivita_label: '',
    numero_barche: '',
    usa_gestionale: '',
    gestionale_attuale: '',
    messaggio: '',
    privacy: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [waUrl, setWaUrl] = useState('');
  const [serverError, setServerError] = useState('');

  useEffect(() => { trackEvent('lead_form_view'); }, []);

  const set = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    if (errors[k]) setErrors(p => { const n = { ...p }; delete n[k]; return n; });
  };

  const validate = () => {
    const e = {};
    if (!form.nome_attivita.trim()) e.nome_attivita = t.errRequired;
    if (!form.nome_referente.trim()) e.nome_referente = t.errRequired;
    if (!form.telefono.trim()) e.telefono = t.errRequired;
    if (!form.email.trim() || !form.email.includes('@')) e.email = t.errInvalidEmail;
    if (!form.tipo_attivita) e.tipo_attivita = t.errSelectType;
    if (!form.numero_barche || isNaN(Number(form.numero_barche)) || Number(form.numero_barche) < 1)
      e.numero_barche = t.errInvalidNumber;
    if (!form.usa_gestionale) e.usa_gestionale = t.errRequired;
    if (!form.privacy) e.privacy = t.errAcceptPrivacy;
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSubmitting(true);
    setServerError('');
    trackEvent('lead_form_submit');

    try {
      await axios.post(`${API}/leads`, {
        nome_attivita: form.nome_attivita.trim(),
        nome_referente: form.nome_referente.trim(),
        telefono: form.telefono.trim(),
        email: form.email.trim(),
        tipo_attivita: form.tipo_attivita,
        numero_barche: Number(form.numero_barche),
        usa_gestionale: form.usa_gestionale,
        gestionale_attuale: form.gestionale_attuale.trim(),
        messaggio: form.messaggio.trim(),
        privacy_accepted: true,
        source: 'web',
        ...utms,
      });

      trackEvent('lead_saved');
      trackLead({ content_name: 'prova-gratis', content_category: form.tipo_attivita });
      const msg = buildWaMessage(form, t);
      const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
      setWaUrl(url);
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setServerError(typeof detail === 'string' ? detail : t.errSaveGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    t.feature1,
    t.feature2,
    t.feature3,
    t.feature4,
    t.feature5,
  ];

  return (
    <div className="min-h-screen bg-[#060E1D]">
      {/* Navbar */}
      <nav className="border-b border-slate-800/60 px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/nautiqo-logo-email-v3.png" alt="Nautiqo" className="h-7 w-auto" onError={e => { e.target.style.display = 'none'; }} />
            <span className="text-white font-bold text-lg">Nautiqo</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-slate-400 hover:text-white transition">
              {t.navAlreadyRegistered} <span className="text-blue-400">{t.navLogin}</span>
            </Link>
            <LanguageSwitcher variant="light" />
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">

          {/* Left: hero — shown below form on mobile, left on desktop */}
          <div className="lg:pt-4 order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-full text-xs text-blue-400 font-medium mb-6">
              {t.heroBadge}
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              {t.heroTitleLine1}<br />
              <span className="text-blue-400">{t.heroTitleLine2}</span>
            </h1>
            <p className="text-slate-400 text-lg mb-8 leading-relaxed">
              {t.heroSubtitle}
            </p>
            <ul className="space-y-3">
              {features.map(f => (
                <li key={f} className="flex items-center gap-3 text-slate-300">
                  <span className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 text-blue-400 text-xs">✓</span>
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Right: form card — shown first on mobile */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 lg:p-8 order-1 lg:order-2">
            {submitted ? (
              /* Success state */
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">✅</span>
                </div>
                <h2 className="text-xl font-bold text-white mb-2">{t.successTitle}</h2>
                <p className="text-slate-400 mb-6">
                  {t.successSubtitle}
                </p>
                <a href={waUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-500 text-white font-semibold rounded-xl transition text-base w-full justify-center mb-3"
                  onClick={() => trackEvent('whatsapp_click')}
                >
                  {t.openWhatsapp}
                </a>
                <p className="text-xs text-slate-500">{t.successHint1} <strong className="text-slate-400">{t.successHint2}</strong>.</p>
              </div>
            ) : (
              /* Form */
              <>
                <h2 className="text-xl font-bold text-white mb-1">{t.formTitle}</h2>
                <p className="text-slate-400 text-sm mb-6">{t.formSubtitle}</p>

                <form onSubmit={handleSubmit} noValidate className="space-y-4">
                  {/* Nome attività */}
                  <div>
                    <label className={lbl}>{t.labelBusinessName} <span className="text-red-400">*</span></label>
                    <input className={errors.nome_attivita ? inpErr : inp} placeholder={t.placeholderBusinessName}
                      autoComplete="organization"
                      value={form.nome_attivita} onChange={e => set('nome_attivita', e.target.value)} />
                    {errors.nome_attivita && <p className="text-red-400 text-xs mt-1">{errors.nome_attivita}</p>}
                  </div>

                  {/* Nome referente */}
                  <div>
                    <label className={lbl}>{t.labelContactName} <span className="text-red-400">*</span></label>
                    <input className={errors.nome_referente ? inpErr : inp} placeholder={t.placeholderContactName}
                      autoComplete="name"
                      value={form.nome_referente} onChange={e => set('nome_referente', e.target.value)} />
                    {errors.nome_referente && <p className="text-red-400 text-xs mt-1">{errors.nome_referente}</p>}
                  </div>

                  {/* Telefono + Email */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={lbl}>{t.labelPhone} <span className="text-red-400">*</span></label>
                      <input className={errors.telefono ? inpErr : inp} placeholder={t.placeholderPhone} type="tel"
                        autoComplete="tel"
                        value={form.telefono} onChange={e => set('telefono', e.target.value)} />
                      {errors.telefono && <p className="text-red-400 text-xs mt-1">{errors.telefono}</p>}
                    </div>
                    <div>
                      <label className={lbl}>{t.labelEmail} <span className="text-red-400">*</span></label>
                      <input className={errors.email ? inpErr : inp} placeholder={t.placeholderEmail} type="email"
                        autoComplete="email"
                        value={form.email} onChange={e => set('email', e.target.value)} />
                      {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>

                  {/* Tipo attività */}
                  <div>
                    <label className={lbl}>{t.labelActivityType} <span className="text-red-400">*</span></label>
                    <select className={errors.tipo_attivita ? inpErr : inp}
                      value={form.tipo_attivita}
                      onChange={e => {
                        const val = e.target.value;
                        const opt = TIPI_ATTIVITA.find(o => o.value === val);
                        set('tipo_attivita', val);
                        set('tipo_attivita_label', opt ? opt.label : val);
                      }}>
                      <option value="">{t.selectPlaceholder}</option>
                      {TIPI_ATTIVITA.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {errors.tipo_attivita && <p className="text-red-400 text-xs mt-1">{errors.tipo_attivita}</p>}
                  </div>

                  {/* Numero barche */}
                  <div>
                    <label className={lbl}>{t.labelNumberBoats} <span className="text-red-400">*</span></label>
                    <input className={errors.numero_barche ? inpErr : inp} placeholder={t.placeholderNumberBoats} type="number" min="1"
                      value={form.numero_barche} onChange={e => set('numero_barche', e.target.value)} />
                    {errors.numero_barche && <p className="text-red-400 text-xs mt-1">{errors.numero_barche}</p>}
                  </div>

                  {/* Usa gestionale */}
                  <div>
                    <label className={lbl}>{t.labelUsesSoftware} <span className="text-red-400">*</span></label>
                    <div className="flex gap-2">
                      {[['no', t.softwareNo], ['si', t.softwareYes], ['si_insoddisfatto', t.softwareYesUnhappy]].map(([val, label]) => (
                        <button key={val} type="button"
                          onClick={() => set('usa_gestionale', val)}
                          className={`flex-1 py-2 px-2 rounded-xl text-xs font-medium border transition ${form.usa_gestionale === val ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                    {errors.usa_gestionale && <p className="text-red-400 text-xs mt-1">{errors.usa_gestionale}</p>}
                  </div>

                  {/* Gestionale attuale (conditional) */}
                  {(form.usa_gestionale === 'si' || form.usa_gestionale === 'si_insoddisfatto') && (
                    <div>
                      <label className={lbl}>{t.labelCurrentSoftware}</label>
                      <input className={inp} placeholder={t.placeholderCurrentSoftware}
                        value={form.gestionale_attuale} onChange={e => set('gestionale_attuale', e.target.value)} />
                    </div>
                  )}

                  {/* Messaggio */}
                  <div>
                    <label className={lbl}>{t.labelMessage} <span className="text-slate-500 font-normal">{t.optional}</span></label>
                    <textarea className={`${inp} resize-none`} rows={3} placeholder={t.placeholderMessage}
                      value={form.messaggio} onChange={e => set('messaggio', e.target.value)} />
                  </div>

                  {/* Privacy */}
                  <div>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input type="checkbox" checked={form.privacy} onChange={e => set('privacy', e.target.checked)}
                        className="mt-0.5 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 flex-shrink-0" />
                      <span className="text-xs text-slate-400 leading-relaxed">
                        {t.privacyText}{' '}
                        <Link to="/privacy" className="text-blue-400 hover:underline">{t.privacyPolicy}</Link>
                        <span className="text-red-400"> *</span>
                      </span>
                    </label>
                    {errors.privacy && <p className="text-red-400 text-xs mt-1">{errors.privacy}</p>}
                  </div>

                  {/* WhatsApp message preview */}
                  {form.nome_attivita && form.nome_referente && form.telefono && form.tipo_attivita && (
                    <div className="p-3 bg-green-500/5 border border-green-500/15 rounded-xl">
                      <p className="text-[11px] text-green-400 font-medium mb-2">{t.waPreviewTitle}</p>
                      <pre className="text-[11px] text-slate-400 whitespace-pre-wrap leading-relaxed font-sans">
                        {buildWaMessage(form, t)}
                      </pre>
                    </div>
                  )}

                  {/* Server error */}
                  {serverError && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                      <p className="text-red-400 text-sm">{serverError}</p>
                    </div>
                  )}

                  {/* Submit */}
                  <button type="submit" disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 py-3.5 bg-green-600 hover:bg-green-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition text-base">
                    {submitting ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        {t.submitting}
                      </>
                    ) : (
                      <>{t.submitButton}</>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
